<script setup lang="ts"></script>
<template>
    <ContentPage title="Declarație de Accesibilitate Fleequid">
        <section class="mb-8">
            <p class="mt-3 text-base">
                Fleequid (Adorea SRL) se angajează să facă propriul site web accesibil, conform legii 9 ianuarie 2004, nr. 4. Prezenta declarație de
                accesibilitate se aplică la:
            </p>
            <p class="mt-2">
                <NuxtLink class="text-primary hover:underline" to="https://fleequid.com" target="_blank" external>https://fleequid.com</NuxtLink>
            </p>
        </section>

        <section class="mb-10">
            <h2 class="text-highlighted text-xl font-semibold md:text-2xl">Accesibilitate</h2>
            <p class="mt-2">
                Obiectivul nostru este să facem conținutul digital accesibil tuturor persoanelor, inclusiv celor cu dizabilități. Ne angajăm să
                îmbunătățim constant experiența de utilizare și să aplicăm cele mai bune practici în domeniul accesibilității, pentru a asigura un
                acces echitabil și incluziv.
            </p>
        </section>

        <section class="mb-10">
            <h2 class="text-highlighted text-xl font-semibold md:text-2xl">Declarație de accesibilitate</h2>
            <p class="mt-2">
                În conformitate cu legislația în vigoare și în concordanță cu valorile noastre de incluziune și egalitate de șanse, am redactat o
                Declarație de accesibilitate oficială. Documentul prezintă nivelul de accesibilitate al site-ului și acțiunile întreprinse pentru
                respectarea Web Content Accessibility Guidelines (WCAG). Declarația completă este disponibilă pentru consultare și descărcare prin
                linkul de mai jos:
            </p>
            <p class="mt-2">
                <a class="text-primary hover:underline" href="/docs/accessibility/accessibility-statement_RO.pdf" target="_blank">
                    Declarație de Accesibilitate Fleequid
                </a>
            </p>
        </section>

        <section class="mb-10">
            <h2 class="text-highlighted text-xl font-semibold md:text-2xl">Starea de conformitate</h2>
            <p class="mt-2">Parțial conform</p>
            <p class="mt-3">
                Acest site web este parțial conform cu cerințele prevăzute în anexa A a normei UNI CEI EN 301549 din cauza cazurilor de neconformitate
                enumerate mai jos.
            </p>
        </section>

        <section class="mb-10">
            <h2 class="text-highlighted text-xl font-semibold md:text-2xl">Conținuturi net accesibile</h2>
            <p class="mt-3">Conținuturile enumerate mai jos nu sunt accesibile din cauza nerespectării legii 9 ianuarie 2004, nr. 4.:</p>

            <h3 class="text-highlighted mt-5 text-lg font-semibold">Pentru paginile web</h3>
            <h4 class="text-highlighted mt-3 font-medium">La nivel general:</h4>
            <ul class="mt-2 list-disc space-y-1 pl-6">
                <li>1.3.1 Informații și corelații (Nivel A)</li>
                <li>1.4.3 Contrast minim (Nivel AA)</li>
                <li>1.4.4 Redimensionarea textului (Nivel AA)</li>
                <li>1.4.10 Recalcularea fluxului (doar nivel AA 2.1)</li>
                <li>2.4.1 Salturi între blocuri (Nivel A)</li>
                <li>2.4.4 Scopul legăturii (în context) (Nivel A)</li>
                <li>2.4.5 Modalități diferite (Nivel AA)</li>
                <li>3.3.1 Identificarea erorilor (Nivel A)</li>
                <li>4.1.2 Nume, rol, valoare (Nivel A)</li>
                <li>4.1.3 Mesaje de stare (Nivel AA wcag 2.1)</li>
            </ul>
        </section>

        <section class="mb-10">
            <h2 class="text-highlighted text-xl font-semibold md:text-2xl">Redactarea declarației de accesibilitate</h2>
            <p class="mt-3">Prezenta declarație a fost redactată la <time datetime="2025-08-20" class="font-bold">20 august 2025</time>.</p>
            <p class="mt-2">
                Informațiile prezentate în această declarație de accesibilitate sunt obținute dintr-o evaluare efectuată de terți, utilizând sisteme
                de validare automată și prin verificări directe, efectuate manual de expertul tehnic în accesibilitate.
            </p>

            <h3 class="text-highlighted mt-5 text-lg font-semibold">Unelte utilizate pentru validarea automată:</h3>
            <ul class="mt-2 list-disc space-y-1 pl-6">
                <li>Deque Axe Accessibility Checker (extensii pentru Firefox și Chrome)</li>
                <li>Siteimprove Accessibility Checker</li>
                <li>WCAG color contrast checker</li>
            </ul>

            <h3 class="text-highlighted mt-5 text-lg font-semibold">Tehnologii asistive:</h3>
            <ul class="mt-2 list-disc space-y-1 pl-6">
                <li>Cititor de ecran: NVDA</li>
            </ul>
        </section>

        <section class="mb-10">
            <h2 class="text-highlighted text-xl font-semibold md:text-2xl">Feedback și informații de contact</h2>
            <p class="mt-3">Se pot trimite, în orice moment, raportări direct la adresa de email: <FleequidEmailLink type="accessibility" /></p>
        </section>

        <section class="mb-10">
            <h2 class="text-highlighted text-xl font-semibold md:text-2xl">Modalități de trimitere a raportărilor către AgID</h2>
            <p class="mt-3">
                În cazul unui răspuns nesatisfăcător sau a lipsei unui răspuns, în termen de treizeci de zile, la notificare sau cerere, persoana
                interesată poate trimite o raportare utilizând modalitatea indicată pe site-ul instituțional AgID.
            </p>
        </section>

        <section class="mb-10">
            <h2 class="text-highlighted text-xl font-semibold md:text-2xl">Informații despre site</h2>
            <dl class="mt-4 space-y-4">
                <div class="grid grid-cols-1 gap-3 sm:grid-cols-3">
                    <dt class="font-medium">Data publicării site-ului web:</dt>
                    <dd class="sm:col-span-2">
                        <time datetime="2024-08-27" class="font-bold">27 august 2024</time>
                    </dd>
                </div>
                <div class="grid grid-cols-1 gap-3 sm:grid-cols-3">
                    <dt class="font-medium">S-au efectuat teste de utilizabilitate:</dt>
                    <dd class="font-bold sm:col-span-2">nu</dd>
                </div>
                <div class="grid grid-cols-1 gap-3 sm:grid-cols-3">
                    <dt class="font-medium">CMS utilizat pentru site-ul web:</dt>
                    <dd class="font-bold sm:col-span-2">Platformă proprietară/custom dezvoltată intern</dd>
                </div>
            </dl>
        </section>

        <section class="mb-4">
            <h2 class="text-highlighted text-xl font-semibold md:text-2xl">Informații despre structură</h2>
            <dl class="mt-4 space-y-4">
                <div class="grid grid-cols-1 gap-3 sm:grid-cols-3">
                    <dt class="font-medium">Numărul de angajați cu dizabilități din administrație:</dt>
                    <dd class="font-bold sm:col-span-2">1</dd>
                </div>
                <div class="grid grid-cols-1 gap-3 sm:grid-cols-3">
                    <dt class="font-medium">Numărul de posturi de lucru pentru angajați cu dizabilități:</dt>
                    <dd class="font-bold sm:col-span-2">0</dd>
                </div>
            </dl>
        </section>
    </ContentPage>
</template>
